/**
 * BookingRequest Component
 * Handles the booking request management interface with consolidated table layout
 * for improved readability and user experience.
 */

import React, { useEffect, useState, useRef } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Layout from "../layout/Layout";
import ContentWrapper from "../components/ContentWrapper";
import LoadingOverlay from "react-loading-overlay-ts";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";
import {
  bookinlist,
  cancelbookingrequest,
  cancelreason,
  updatebookingcarer,
} from "../Redux/Action/BookingAction";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import Flag from "react-flagkit";
import Contry from "../Component/Contry";
import {
  carersAvailability,
} from "../Redux/Action/CareSeekerAction";
import { Modal } from "react-bootstrap";
import Searchable from "react-searchable-dropdown";
import { useFormik } from "formik";
import * as Yup from "yup";
import FirstLetterCapital from "../CustomHooks/FirstLetterCapital";
import defaultAvatar from "../assets/images/default-avatar.png";

// Add styles for the consolidated table
const styles = {
  tableContainer: {
    margin: '20px 0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff'
  },
  th: {
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    textAlign: 'left'
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #dee2e6',
    verticalAlign: 'top'
  },
  bookingInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  bookingNumber: {
    fontSize: '1.1em',
    fontWeight: '600',
    color: '#2c3e50'
  },
  bookingDate: {
    fontSize: '0.85em',
    color: '#6c757d'
  },
  careSeekerInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  careSeekerDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  careSeekerName: {
    fontWeight: '500',
    color: '#2c3e50'
  },
  careSeekerContact: {
    fontSize: '0.85em',
    color: '#6c757d'
  },
  flightInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  preferencesInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  paymentInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontWeight: '500',
    color: '#495057'
  },
  value: {
    color: '#212529'
  },
  actions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  }
};

const getStatusColors = (status) => {
  switch (status) {
    case 'payment_confirmed': return { backgroundColor: '#d4edda', color: '#155724' };
    case 'booking_request_initiated': return { backgroundColor: '#cce5ff', color: '#004085' };
    case 'accepted_by_carer': return { backgroundColor: '#d1ecf1', color: '#0c5460' };
    case 'carer_assigned': return { backgroundColor: '#e2e3e5', color: '#383d41' };
    case 'started': return { backgroundColor: '#cce5ff', color: '#004085' };
    case 'completed': return { backgroundColor: '#d4edda', color: '#155724' };
    case 'cancellation_pending': return { backgroundColor: '#fff3cd', color: '#856404' };
    case 'cancellation_accepted': return { backgroundColor: '#f8d7da', color: '#721c24' };
    case 'rejected_by_admin': return { backgroundColor: '#f8d7da', color: '#721c24' };
    case 'cancellation_completed': return { backgroundColor: '#d6d8db', color: '#383d41' };
    default: return { backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6' };
  }
};

export default function BookingRequest() {
  const [loading, setLoading] = useState(true); // Loader state
  const [show, setshow] = useState(false);
  const [id, setid] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [depFrom, setDepFrom] = useState("");
  const [depTo, setDepTo] = useState("");
  const [statusFilter, setStatusFilter] = useState(["payment_confirmed"]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [matchFlightNumber, setMatchFlightNumber] = useState(false);
  const [matchPlanType, setMatchPlanType] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [carerSearch, setCarerSearch] = useState("");
  const [carerGender, setCarerGender] = useState("");
  const [carerLanguage, setCarerLanguage] = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  const [booing_by, setbooing_by] = useState("");

  const [canclerequestshow, setCancleRequestShow] = useState(false);

  const dispatch = useDispatch();
  const statusDropdownRef = useRef(null);
  const [searchParams] = useSearchParams();
  
  // Track if current filters came from URL parameters (don't persist these)
  const [filtersFromURL, setFiltersFromURL] = useState(false);
  
  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let initialStatus = ["payment_confirmed"];
    let initialSearch = "";
    let initialPage = 1;
    let initialLimit = 10;
    let initialSortBy = "createdAt";
    let initialSortOrder = "desc";
    let initialDateFrom = "";
    let initialDateTo = "";
    let initialDepFrom = "";
    let initialDepTo = "";
    let fromURL = false;

    // First, check for URL parameters (these take priority and won't be persisted)
    if (searchParams.size > 0) {
      fromURL = true;
      
      // Parse URL parameters
      if (searchParams.get('search')) {
        initialSearch = searchParams.get('search');
        setSearch(initialSearch);
      }
      
      if (searchParams.get('page')) {
        const pageParam = parseInt(searchParams.get('page'));
        if (!isNaN(pageParam) && pageParam > 0) {
          initialPage = pageParam;
          setPage(initialPage);
        }
      }
      
      if (searchParams.get('limit')) {
        const limitParam = parseInt(searchParams.get('limit'));
        if (!isNaN(limitParam) && [10, 15, 20, 50].includes(limitParam)) {
          initialLimit = limitParam;
          setLimit(initialLimit);
        }
      }
      
      if (searchParams.get('sortBy')) {
        initialSortBy = searchParams.get('sortBy');
        setSortBy(initialSortBy);
      }
      
      if (searchParams.get('sortOrder')) {
        const orderParam = searchParams.get('sortOrder');
        if (['asc', 'desc'].includes(orderParam)) {
          initialSortOrder = orderParam;
          setSortOrder(initialSortOrder);
        }
      }
      
      if (searchParams.get('dateFrom')) {
        initialDateFrom = searchParams.get('dateFrom');
        setDateFrom(initialDateFrom);
      }
      
      if (searchParams.get('dateTo')) {
        initialDateTo = searchParams.get('dateTo');
        setDateTo(initialDateTo);
      }
      
      if (searchParams.get('depFrom')) {
        initialDepFrom = searchParams.get('depFrom');
        setDepFrom(initialDepFrom);
      }
      
      if (searchParams.get('depTo')) {
        initialDepTo = searchParams.get('depTo');
        setDepTo(initialDepTo);
      }
      
      if (searchParams.get('status')) {
        const statusParam = searchParams.get('status');
        try {
          // Handle both single status and comma-separated multiple statuses
          const statusArray = statusParam.includes(',') 
            ? statusParam.split(',').map(s => s.trim()).filter(Boolean)
            : [statusParam];
          
          if (statusArray.length > 0) {
            initialStatus = statusArray;
            setStatusFilter(initialStatus);
          }
        } catch (e) {
          console.warn('Invalid status parameter:', statusParam);
        }
      }
      
      setFiltersFromURL(true);
    } else {
      // No URL parameters, check session storage
      const persisted = sessionStorage.getItem("bookingRequestFilters");
      
      if (persisted) {
        try {
          const p = JSON.parse(persisted);
          initialSearch = p.search || "";
          initialPage = p.page || 1;
          initialLimit = p.limit || 10;
          initialSortBy = p.sortBy || "createdAt";
          initialSortOrder = p.sortOrder || "desc";
          initialDateFrom = p.dateFrom || "";
          initialDateTo = p.dateTo || "";
          initialDepFrom = p.depFrom || "";
          initialDepTo = p.depTo || "";
          
          setSearch(initialSearch);
          setPage(initialPage);
          setLimit(initialLimit);
          setSortBy(initialSortBy);
          setSortOrder(initialSortOrder);
          setDateFrom(initialDateFrom);
          setDateTo(initialDateTo);
          setDepFrom(initialDepFrom);
          setDepTo(initialDepTo);
          
          if (p.statusFilter && Array.isArray(p.statusFilter)) {
            initialStatus = p.statusFilter;
          }
          setStatusFilter(initialStatus);
        } catch (e) {
          console.warn('Error parsing persisted filters:', e);
        }
      }
      
      setFiltersFromURL(false);
    }

    // Build initial payload for API call
    const initPayload = {
      search: initialSearch,
      page: initialPage,
      limit: initialLimit,
      sort_by: initialSortBy,
      sort_order: initialSortOrder,
      status: initialStatus
    };
    
    if (initialDateFrom) initPayload.date_from = initialDateFrom;
    if (initialDateTo) initPayload.date_to = initialDateTo;
    if (initialDepFrom) initPayload.departure_from = initialDepFrom;
    if (initialDepTo) initPayload.departure_to = initialDepTo;
    
    dispatch(bookinlist(initPayload)).then(() => setLoading(false));
    dispatch(cancelreason());
  }, [searchParams, dispatch]);
  const list = useSelector((state) => state.Booking.booking);
  // Fetch available carers when opening the assign modal for a specific booking
  const openAssignModal = (booking) => {
    setshow(true);
    setid(booking?._id);
    setbooing_by(booking?.care_seeker?._id);
    setCurrentBooking(booking);
    const origin = booking?.flight_details?.departure_airport || "";
    const destination = booking?.flight_details?.destination_airport || "";
    const travel_date = booking?.flight_details?.departure_date || "";
    const flight_number = booking?.flight_details?.flight_number || "";
    dispatch(
      carersAvailability({
        origin,
        destination,
        travel_date,
        flight_number,
      })
    );
  };

  const userList = useSelector((state) => state.careseeker.carerlist);
  const resasonlist = useSelector((state) => state.Booking.resasonlist);

  //resasonlist
  const handleClose = () => {
    setshow(false);
    setid("");
  };

  //updatebookingcarer

  const formik = useFormik({
    initialValues: {
      carer_id: "",
    },
    validationSchema: Yup.object().shape({
      carer_id: Yup.string().required("Carer is required"),
    }),
    onSubmit: (value) => {
      setLoading(true);
      value.booking_id = id;

      dispatch(updatebookingcarer(value)).then(function (res) {
        if (res?.payload) {
          toast.success("Carer assigned successfully");
        }
        dispatch(
          bookinlist({
            search: "",
            status: "payment_confirmed",
            page: 1,
            limit: 15,
          })
        ).then(() => setLoading(false));
        setshow(false);
        setid("");
        formik.resetForm();
      });
    },
  });

  const cancelFormik = useFormik({
    initialValues: {
      reason_id: "",
      reason: "",
    },
    validationSchema: Yup.object().shape({
      reason_id: Yup.string().required("Reason id is required"),
      reason: Yup.string().required("Description is required"),
    }),
    onSubmit: async (value) => {
      setLoading(true);
      value.booking_id = id;

      try {
        const res = await dispatch(cancelbookingrequest(value));
        if (res?.payload) {
          toast.success("Booking request cancelled successfully");
          // Use fetchList to respect current filters
          fetchList(); 
        } else {
            // Failed
            setLoading(false);
        }
      } catch (e) {
          console.error(e);
          setLoading(false);
      } finally {
        setCancleRequestShow(false);
        setid("");
        formik.resetForm();
      }
    },
  });

  const persistFilters = (overrides = {}) => {
    // Don't persist filters if they came from URL parameters
    if (filtersFromURL) {
      return;
    }
    
    const data = {
      search,
      page,
      limit,
      sortBy,
      sortOrder,
      dateFrom,
      dateTo,
      depFrom,
      depTo,
      statusFilter,
      ...overrides,
    };
    sessionStorage.setItem("bookingRequestFilters", JSON.stringify(data));
  };

  const fetchList = (overrides = {}) => {
    setLoading(true);
    
    // Determine effective values (override takes precedence over state)
    const effectiveSearch = overrides.hasOwnProperty('search') ? overrides.search : search;
    const effectivePage = overrides.hasOwnProperty('page') ? overrides.page : page;
    const effectiveLimit = overrides.hasOwnProperty('limit') ? overrides.limit : limit;
    const effectiveSortBy = overrides.hasOwnProperty('sort_by') ? overrides.sort_by : sortBy;
    const effectiveSortOrder = overrides.hasOwnProperty('sort_order') ? overrides.sort_order : sortOrder;
    const effectiveDateFrom = overrides.hasOwnProperty('date_from') ? overrides.date_from : dateFrom;
    const effectiveDateTo = overrides.hasOwnProperty('date_to') ? overrides.date_to : dateTo;
    const effectiveDepFrom = overrides.hasOwnProperty('departure_from') ? overrides.departure_from : depFrom;
    const effectiveDepTo = overrides.hasOwnProperty('departure_to') ? overrides.departure_to : depTo;
    const effectiveStatus = overrides.hasOwnProperty('status') ? overrides.status : statusFilter;

    const payload = {
      search: effectiveSearch,
      page: effectivePage,
      limit: effectiveLimit,
      sort_by: effectiveSortBy,
      sort_order: effectiveSortOrder,
    };

    if (effectiveDateFrom) payload.date_from = effectiveDateFrom;
    if (effectiveDateTo) payload.date_to = effectiveDateTo;
    if (effectiveDepFrom) payload.departure_from = effectiveDepFrom;
    if (effectiveDepTo) payload.departure_to = effectiveDepTo;
    if (Array.isArray(effectiveStatus) && effectiveStatus.length) payload.status = effectiveStatus;

    dispatch(bookinlist(payload)).then(() => setLoading(false));
    persistFilters({
        ...payload,
        // Ensure persistFilters saves the raw effective values for state restoration
        dateFrom: effectiveDateFrom,
        dateTo: effectiveDateTo,
        depFrom: effectiveDepFrom,
        depTo: effectiveDepTo,
        statusFilter: effectiveStatus,
        sortBy: effectiveSortBy,
        sortOrder: effectiveSortOrder
    });
  };

  const handelsearch = () => {
    setFiltersFromURL(false); // Reset URL flag when user manually searches
    if (search !== "") {
      fetchList({ search });
    } else {
      fetchList({ search: "" });
    }
  };

  const exportcsv = async () => {
    const header = [
      {
        id: "booking_number",
        title: "Booking Number",
      },
      {
        id: "care_seeker",
        title: "Care Seeker Name",
      },
      {
        id: "care_seeker_email",
        title: "Care Seeker Email",
      },
      {
        id: "care_seeker_phone",
        title: "Care Seeker Phone",
      },
      {
        id: "booking_status",
        title: "Request Status",
      },
      {
        id: "flight_number",
        title: "Flight Number",
      },
      {
        id: "departure_date",
        title: "Departure Date",
      },
      {
        id: "departure_airport",
        title: "Departure Airport",
      },
      {
        id: "destination_airport",
        title: "Destination Airport",
      },
      {
        id: "plan_type",
        title: "Plan Type",
      },
      {
        id: "preferences_language",
        title: "Language Preferences",
      },
      {
        id: "preferences_gender",
        title: "Gender Preference",
      },
      {
        id: "preferences_medical",
        title: "Medical Background Required",
      },
      {
        id: "total_amount",
        title: "Total Amount",
      },
      {
        id: "subtotal",
        title: "Subtotal",
      },
      {
        id: "createdAt",
        title: "Created At",
      },
    ];

    // Build export payload according to backend validation requirements
    const exportPayload = {
      header,
      status: statusFilter.length > 0 ? statusFilter[0] : "payment_confirmed", // Backend expects single status string
    };

    // Add optional filters only if they have values
    if (dateFrom) exportPayload.date_from = dateFrom;
    if (dateTo) exportPayload.date_to = dateTo;
    if (depFrom) exportPayload.departure_from = depFrom;
    if (depTo) exportPayload.departure_to = depTo;

    // Custom export function that doesn't show toast errors
    const tryServerExport = async (payload) => {
      try {
        const response = await fetch('/api/v1/admin/export-booking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('authorization') ? `Bearer ${localStorage.getItem('authorization')}` : ''
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          const data = await response.json();
          return data.data;
        }
        return null;
      } catch (error) {
        return null;
      }
    };

    try {
      setExportLoading(true);
      
      // Try server export first
      const serverResult = await tryServerExport(exportPayload);
      
      if (serverResult) {
        // Server export successful
        const link = document.createElement('a');
        link.href = serverResult;
        link.download = `booking_requests_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Server export failed, use client-side export
        console.log('Server export failed, using client-side export');
        exportToCSVFallback(list?.data || [], `booking_requests_export_${new Date().toISOString().split('T')[0]}.csv`);
      }
    } catch (error) {
      console.log('Export error, using client-side export:', error.message);
      // Fallback to client-side export
      exportToCSVFallback(list?.data || [], `booking_requests_export_${new Date().toISOString().split('T')[0]}.csv`);
    } finally {
      setExportLoading(false);
    }
  };
  const handlePageClick = (e) => {
    setPage(e.selected + 1);
    setFiltersFromURL(false); // Reset URL flag when user manually changes page
    fetchList({ page: e.selected + 1 });
  };
  const handleCancelBookingClose = () => {
    setCancleRequestShow(false);
    setid("");
  };

  // Modern clear search
  const clearSearch = () => {
    setSearch("");
    setPage(1);
    setStatusFilter(["payment_confirmed"]);
    setDateFrom("");
    setDateTo("");
    setDepFrom("");
    setDepTo("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setLoading(true);
    setFiltersFromURL(false); // Reset URL flag when clearing
    sessionStorage.removeItem("bookingRequestFilters");
    dispatch(
      bookinlist({
        search: "",
        page: 1,
        limit: 15,
        sort_by: "createdAt",
        sort_order: "desc",
        status: ["payment_confirmed"]
      })
    ).then(() => setLoading(false));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handelsearch();
    }
  };

  // Status options for booking requests
  const statusOptions = [
    { label: "Booking Request Initiated", value: "booking_request_initiated" },
    { label: "Payment Confirmed", value: "payment_confirmed" },
    { label: "Accepted By Carer", value: "accepted_by_carer" },
    { label: "Carer Assigned", value: "carer_assigned" },
    { label: "Trip Started", value: "started" },
    { label: "Completed", value: "completed" },
    { label: "Cancellation Pending", value: "cancellation_pending" },
    { label: "Cancellation Accepted", value: "cancellation_accepted" },
    { label: "Cancellation Completed", value: "cancellation_completed" },
    { label: "Rejected By Admin", value: "rejected_by_admin" }
  ];

  // Handle status selection
  const handleStatusSelect = (status) => {
    let newStatusFilter;
    if (statusFilter.includes(status)) {
      newStatusFilter = statusFilter.filter(s => s !== status);
    } else {
      newStatusFilter = [...statusFilter, status];
    }
    setStatusFilter(newStatusFilter);
    setFiltersFromURL(false); // Reset URL flag when user manually changes filters
    fetchList({ status: newStatusFilter });
  };

  // Handle status removal
  const handleStatusRemove = (status) => {
    const newStatusFilter = statusFilter.filter(s => s !== status);
    setStatusFilter(newStatusFilter);
    setFiltersFromURL(false); // Reset URL flag when user manually changes filters
    fetchList({ status: newStatusFilter });
  };

  // Helper function to get active filters summary
  const getActiveFilters = () => {
    const filters = [];
    if (search) filters.push(`Search: "${search}"`);
    if (statusFilter.length > 0) {
      if (statusFilter.length === 1) {
        const opt = statusOptions.find(o => o.value === statusFilter[0]);
        filters.push(`Status: ${opt ? opt.label : statusFilter[0]}`);
      } else {
        filters.push(`Status: ${statusFilter.length} selected`);
      }
    }
    if (dateFrom) filters.push(`From: ${new Date(dateFrom).toLocaleDateString()}`);
    if (dateTo) filters.push(`To: ${new Date(dateTo).toLocaleDateString()}`);
    if (depFrom) filters.push(`Dep From: ${new Date(depFrom).toLocaleDateString()}`);
    if (depTo) filters.push(`Dep To: ${new Date(depTo).toLocaleDateString()}`);
    return filters;
  };

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // Fallback client-side CSV export function
  const exportToCSVFallback = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const csvHeaders = [
      'Booking Number',
      'Care Seeker Name',
      'Care Seeker Email',
      'Care Seeker Phone',
      'Request Status',
      'Flight Number',
      'Departure Date',
      'Departure Airport',
      'Destination Airport',
      'Plan Type',
      'Language Preferences',
      'Gender Preference',
      'Medical Background Required',
      'Total Amount',
      'Subtotal',
      'Created At'
    ];

    const csvRows = data.map(booking => [
      booking.booking_number || '',
      booking.care_seeker ? `${booking.care_seeker.first_name} ${booking.care_seeker.last_name}` : '',
      booking.care_seeker?.email || '',
      booking.care_seeker?.phone ? `${booking.care_seeker.phone.country_code} ${booking.care_seeker.phone.number}` : '',
      booking.booking_status ? booking.booking_status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '',
      booking.flight_details?.flight_number || '',
      booking.flight_details?.departure_date ? new Date(booking.flight_details.departure_date).toLocaleDateString() : '',
      booking.flight_details?.departure_airport || '',
      booking.flight_details?.destination_airport || '',
      booking.plan_type || '',
      booking.preferences?.language?.map(lang => lang.name).join(', ') || '',
      booking.preferences?.gender || '',
      booking.preferences?.medical_background ? 'Yes' : 'No',
      booking.payment_details?.total_amount ? `$${booking.payment_details.total_amount.toFixed(2)}` : '',
      booking.payment_details?.subtotal ? `$${booking.payment_details.subtotal.toFixed(2)}` : '',
      booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : ''
    ]);

    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  console.log(userList, "successResponse");
  const flightNumber = currentBooking?.flight_details?.flight_number;
  const bookingPlan = currentBooking?.plan_type;
  const filteredCarers = Array.isArray(userList) ? userList.filter((object) => {
    if (booing_by === object?._id) return false;
    const fnOk =
      !matchFlightNumber ||
      (object?.flight_details?.flight_number &&
        object.flight_details.flight_number === flightNumber);
    const packages =
      object?.flight_details?.packages ||
      object?.flight_details?.offered_plan_types ||
      [];
    const hasAllThree =
      Array.isArray(packages) &&
      ["premium", "medium", "basic"].every((p) => packages.includes(p));
    const planOk =
      !matchPlanType ||
      !bookingPlan ||
      hasAllThree ||
      (Array.isArray(packages) && packages.includes(bookingPlan));
    return fnOk && planOk;
  }) : [];
  const displayedCarers = Array.isArray(filteredCarers) ? filteredCarers.filter((object) => {
    const q = (carerSearch || "").trim().toLowerCase();
    const genderOk = !carerGender || (object?.gender || "").toLowerCase() === carerGender.toLowerCase();
    const langOk = !carerLanguage || (Array.isArray(object?.language_preferences) && object.language_preferences.some((l) => {
      const s = typeof l === "string" ? l : (l?.name || "");
      return s.toLowerCase().includes(carerLanguage.toLowerCase());
    }));
    if (!q) return genderOk && langOk;
    const name = `${object?.first_name || ""} ${object?.last_name || ""}`.toLowerCase();
    const email = (object?.email || "").toLowerCase();
    const phone = `${object?.phone?.country_code || ""}${object?.phone?.number || ""}`.toLowerCase();
    const fn = (object?.flight_details?.flight_number || "").toLowerCase();
    const matches = name.includes(q) || email.includes(q) || phone.includes(q) || fn.includes(q);
    return matches && genderOk && langOk;
  }) : [];
  return (
    <Layout>
      <style>
        {`
          .assign-carer-modal .modal-dialog {
            max-width: 92vw !important;
          }
          .assign-carer-modal .modal-body {
            padding: 18px;
          }
          .assign-carer-filter-bar {
            background: #ffffff;
            border: 1px solid #e9ecef;
            border-radius: 10px;
            padding: 12px;
            margin-bottom: 12px;
            box-shadow: 0 6px 14px rgba(0,0,0,0.08);
          }
          .assign-carer-filter-bar input,
          .assign-carer-filter-bar select {
            background: #fff !important;
            border: 1px solid #ced4da !important;
            color: #212529 !important;
          }
          .assign-carer-filter-bar label {
            color: #2c3e50;
            font-weight: 500;
            margin-right: 8px;
          }

          /* Mobile Table Email Fix */
          @media (max-width: 640px) {
            .responsive-table .care-seeker-email {
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        `}
      </style>
      <LoadingOverlay
        active={loading}
        spinner
        text="Loading data..."
        styles={{
          overlay: (base) => ({
            ...base,
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
          }),
        }}
      >
        <ContentWrapper title="Booking Requests">
            {/* Collapsible Filter Bar */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
              {/* Filter Header - Always Visible */}
              <div 
                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                <div className="flex items-center space-x-3 mb-3 sm:mb-0">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                    {!filtersExpanded && hasActiveFilters && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {activeFilters.map((filter, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                            {filter}
                          </span>
                        ))}
                      </div>
                    )}
                    {!filtersExpanded && !hasActiveFilters && (
                      <p className="text-sm text-gray-500">Click to expand filter options</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end space-x-3">
                  <div className="text-sm text-gray-500">
                    {list?.totalItems || 0} total requests
                  </div>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expandable Filter Content */}
              {filtersExpanded && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  {/* Search Bar */}
                  <div className="mb-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search Booking Requests
                      </span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search by booking ID or care seeker name..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          if (e.target.value === "") {
                            clearSearch();
                          }
                        }}
                        onKeyDown={handleSearchKeyDown}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      />
                      {search && (
                        <button
                          onClick={clearSearch}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filter Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Request Status
                        </span>
                      </label>
                      <div className="relative" ref={statusDropdownRef}>
                        <div
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 flex items-center justify-between min-h-[42px] transition-colors"
                          onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                        >
                          <div className="flex flex-wrap gap-1">
                            {statusFilter.length === 0 && (
                              <span className="text-gray-500">Select request status...</span>
                            )}
                            {statusFilter.map((status) => {
                              const opt = statusOptions.find((o) => o.value === status);
                              return (
                                <span key={status} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                                  {opt?.label}
                                  <button
                                    className="ml-1 text-emerald-600 hover:text-emerald-800 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); handleStatusRemove(status); }}
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {statusDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                            {statusOptions.map((opt) => (
                              <div
                                key={opt.value}
                                className={`px-3 py-2 cursor-pointer hover:bg-emerald-50 flex items-center justify-between transition-colors ${
                                  statusFilter.includes(opt.value) ? "bg-emerald-50 text-emerald-700" : "text-gray-700"
                                }`}
                                onClick={(e) => { e.stopPropagation(); handleStatusSelect(opt.value); }}
                              >
                                <span className="flex items-center">
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    opt.value === 'completed' ? 'bg-green-500' :
                                    opt.value === 'carer_assigned' ? 'bg-blue-500' :
                                    opt.value === 'payment_confirmed' ? 'bg-emerald-500' :
                                    opt.value.includes('cancellation') || opt.value === 'rejected_by_admin' ? 'bg-red-500' : 'bg-yellow-500'
                                  }`}></div>
                                  {opt.label}
                                </span>
                                {statusFilter.includes(opt.value) && (
                                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Click to select multiple statuses</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Created From
                        </span>
                      </label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => {
                          setDateFrom(e.target.value);
                          setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                          fetchList({ date_from: e.target.value || undefined });
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Created To
                        </span>
                      </label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => {
                          setDateTo(e.target.value);
                          setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                          fetchList({ date_to: e.target.value || undefined });
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Additional Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Departure From
                        </span>
                      </label>
                      <input
                        type="date"
                        value={depFrom}
                        onChange={(e) => {
                          setDepFrom(e.target.value);
                          setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                          fetchList({ departure_from: e.target.value || undefined });
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Departure To
                        </span>
                      </label>
                      <input
                        type="date"
                        value={depTo}
                        onChange={(e) => {
                          setDepTo(e.target.value);
                          setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                          fetchList({ departure_to: e.target.value || undefined });
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707L16.414 6.5A1 1 0 0015.707 6H7a2 2 0 00-2 2v11a2 2 0 002 2z" />
                          </svg>
                          Items per Page
                        </span>
                      </label>
                      <select
                        value={limit}
                        onChange={(e) => {
                          setLimit(Number(e.target.value));
                          setPage(1);
                          setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                          fetchList({ limit: Number(e.target.value), page: 1 });
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white"
                      >
                        {[10,15,20,50].map(n => (<option key={n} value={n}>{n} items</option>))}
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <button
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        onClick={clearSearch}
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Clear All Filters
                      </button>
                      
                      {hasActiveFilters && (
                        <div className="text-sm text-gray-500">
                          {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} active
                        </div>
                      )}
                    </div>

                    <button 
                      className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      onClick={exportcsv}
                      disabled={exportLoading}
                    >
                      {exportLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export CSV ({list?.data?.length || 0})
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Enhanced Booking Requests Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Table Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Booking Requests ({list?.totalItems || 0})
                </h3>
              </div>

              {/* Table Content */}
              <div className="overflow-x-auto">
                <table className="w-full responsive-table">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th 
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors"
                        onClick={() => {
                          const nextOrder = sortOrder === "asc" ? "desc" : "asc";
                          setSortBy("createdAt");
                          setSortOrder(nextOrder);
                          setFiltersFromURL(false); // Reset URL flag when user manually sorts
                          fetchList({ sort_by: "createdAt", sort_order: nextOrder });
                        }}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Booking Info</span>
                          {sortBy === "createdAt" && (
                            <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Care Seeker
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors"
                        onClick={() => {
                          const nextOrder = sortOrder === "asc" ? "desc" : "asc";
                          setSortBy("flight_details.departure_date");
                          setSortOrder(nextOrder);
                          setFiltersFromURL(false); // Reset URL flag when user manually sorts
                          fetchList({ sort_by: "flight_details.departure_date", sort_order: nextOrder });
                        }}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Flight Details</span>
                          {sortBy === "flight_details.departure_date" && (
                            <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Preferences
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {!list?.data || list.data.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No booking requests found</h3>
                          <p className="mt-1 text-sm text-gray-500">No booking requests match your current filters.</p>
                        </td>
                      </tr>
                    ) : (
                    list.data.map(function (object, i) {
                    const isCompleted = object.booking_status === "completed";
                    const isAssigned = object.booking_status === "carer_assigned";
                    const isAccepted = object.booking_status === "accepted_by_carer";
                    const isCancelled = object.booking_status === "rejected_by_admin" || object.booking_status === "cancellation_completed" || object.booking_status === "cancellation_accepted";

                    return (
                      <tr
                        key={object?._id || i}
                        className={
                          "userlist-table-row userlist-table-animate " +
                          (i % 2 === 0 ? "userlist-table-row-even" : "userlist-table-row-odd")
                        }
                      >
                        <td data-label="Booking Info" style={styles.td}>
                          <div style={styles.bookingInfo}>
                            <div style={styles.bookingNumber}>
                              <a
                                href={object?._id ? `/booking-details/${object._id}` : undefined}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                #{object?.booking_number}
                              </a>
                            </div>
                            <div style={styles.bookingDate}>
                              Created: {moment(object?.createdAt).format("DD-MM-YYYY")}
                            </div>
                            <div style={{ marginTop: '6px' }}>
                              {object?.booking_status ? (
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  display: 'inline-block',
                                  textTransform: 'capitalize',
                                  ...getStatusColors(object.booking_status)
                                }}>
                                  {object.booking_status.split('_').join(' ')}
                                </span>
                              ) : 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td data-label="Care Seeker" style={styles.td}>
                          <div style={styles.careSeekerInfo}>
                            <a
                              href={object?.care_seeker?._id ? `/users/${object.care_seeker._id}` : undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', color: 'inherit' }}
                              className="care-seeker-email"
                            >
                              <img
                                src={object?.care_seeker?.profile_picture || defaultAvatar}
                                alt="profile"
                                width="40"
                                height="40"
                                onError={(e) => { e.currentTarget.src = defaultAvatar; }}
                                style={{ borderRadius: '50%', marginRight: 8 }}
                              />
                              <div style={styles.careSeekerDetails}>
                                <div style={styles.careSeekerName}>
                                  {FirstLetterCapital(
                                    object?.care_seeker?.first_name +
                                      " " +
                                      object?.care_seeker?.last_name
                                  )}
                                  <i
                                    className={object?.care_seeker?.shufti_verification ? "fas fa-check-circle" : "fas fa-times-circle"}
                                    data-tooltip-id="verification-tip"
                                    data-tooltip-content={object?.care_seeker?.shufti_verification ? "Verified user" : "Not verified"}
                                    style={{
                                      marginLeft: 6,
                                      fontSize: "1rem",
                                      color: object?.care_seeker?.shufti_verification ? "#16a34a" : "#dc2626",
                                      lineHeight: 1
                                    }}
                                  />
                                </div>
                                <div style={styles.careSeekerContact}>
                                  {object?.care_seeker?.email}
                                </div>
                                <div style={styles.careSeekerContact}>
                                  {object?.care_seeker?.phone?.country_code && (
                                    <>
                                      <Flag
                                        country={Contry(object?.care_seeker?.phone?.country_code)}
                                        size={16}
                                      />
                                      {" " + object?.care_seeker?.phone?.country_code +
                                        " " +
                                        object?.care_seeker?.phone?.number}
                                    </>
                                  )}
                                </div>
                              </div>
                            </a>
                          </div>
                        </td>
                        <td data-label="Flight Details" style={styles.td}>
                          <div style={styles.flightInfo}>
                            <div>
                              <span style={styles.label}>Flight:</span>{" "}
                              <span style={styles.value}>{object?.flight_details?.flight_number || 'N/A'}</span>
                              {object?.flight_details?.is_manual_flight && (
                                <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.72rem' }}>⚠️ Local</span>
                              )}
                            </div>
                            <div>
                              <span style={styles.label}>Plan Type:</span>{" "}
                              <span style={styles.value}>{object?.plan_type || 'N/A'}</span>
                            </div>
                            <div>
                              <span style={styles.label}>Departure:</span>{" "}
                              <span style={styles.value}>
                                {object?.flight_details?.departure_date
                                  ? moment(object.flight_details.departure_date).format("DD-MM-YYYY")
                                  : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td data-label="Preferences" style={styles.td}>
                          <div style={styles.preferencesInfo}>
                            <div>
                              <span style={styles.label}>Languages:</span>{" "}
                              <span style={styles.value}>
                                {object.preferences?.language
                                  ?.map(lang => FirstLetterCapital(lang.name))
                                  .join(", ") || 'N/A'}
                              </span>
                            </div>
                            <div>
                              <span style={styles.label}>Gender:</span>{" "}
                              <span style={styles.value}>{FirstLetterCapital(object?.preferences?.gender) || 'N/A'}</span>
                            </div>
                            <div>
                              <span style={styles.label}>Medical:</span>{" "}
                              <span style={styles.value}>{object?.preferences?.medical_background ? "Yes" : "No"}</span>
                            </div>
                          </div>
                        </td>
                        <td data-label="Payment" style={styles.td}>
                          <div style={styles.paymentInfo}>
                            <div>
                              <span style={styles.label}>Total:</span>{" "}
                              <span style={styles.value}>
                                {(() => {
                                  const pd = object?.payment_details || {};
                                  const total = typeof pd.total_amount === "number" ? pd.total_amount : 0;
                                  return Number(Math.max(0, total)).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  });
                                })()}
                              </span>
                            </div>
                            <div>
                              <span style={styles.label}>Subtotal:</span>{" "}
                              <span style={styles.value}>
                                {(() => {
                                  const pd = object?.payment_details || {};
                                  const hasSub = typeof pd.subtotal === "number" && isFinite(pd.subtotal);
                                  const subCandidate = hasSub
                                    ? pd.subtotal
                                    : (typeof pd.total_amount === "number" ? pd.total_amount : 0) -
                                      (typeof pd.platform_booking_charge === "number" ? pd.platform_booking_charge : 0);
                                  const subValue = Math.max(0, Number(subCandidate));
                                  return subValue.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  });
                                })()}
                              </span>
                            </div>
                            {Number(object?.payment_details?.discount || 0) > 0 && (
                              <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: '500' }}>
                                <span style={styles.label}>Discount:</span> -${Number(object.payment_details.discount).toFixed(2)}
                              </div>
                            )}
                            {Number(object?.payment_details?.low_order_fee || 0) > 0 && (
                              <div style={{ fontSize: '11px', color: '#2563eb', fontWeight: '500' }}>
                                <span style={styles.label}>Min Floor:</span> +${Number(object.payment_details.low_order_fee).toFixed(2)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td data-label="Actions" style={styles.td}>
                          <div style={styles.actions}>
                            {isCompleted ? (
                              <div className="flex items-center justify-center">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Completed
                                </span>
                              </div>
                            ) : isAssigned ? (
                              <div className="flex items-center justify-center">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                                  </svg>
                                  Assigned
                                </span>
                              </div>
                            ) : isAccepted ? (
                              <div className="flex items-center justify-center">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Accepted
                                </span>
                              </div>
                            ) : isCancelled ? (
                              <div className="flex items-center justify-center">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  Cancelled
                                </span>
                              </div>
                            ) : object?.bookingreq?.length === 0 || !object?.bookingreq.some(
                                (bookingrequestobject) =>
                                  bookingrequestobject.approval_status === "pending"
                              ) ? (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openAssignModal(object)}
                                  className="inline-flex items-center px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 transition-all duration-200 shadow-sm"
                                  title="Assign Carer"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                  Assign
                                </button>
                                <button
                                  onClick={() => {
                                    setCancleRequestShow(true);
                                    setid(object?._id);
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200 shadow-sm"
                                  title="Cancel Request"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  Pending
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>
              
              <Pagination
                handlePageClick={handlePageClick}
                pageCount={Math.ceil((list?.totalItems || 0) / (limit || 10))}
              />
            </div>
        </ContentWrapper>
        
        <Modal show={show} onHide={handleClose} size="xl" dialogClassName="assign-carer-modal">
          <Modal.Header closeButton>
            <Modal.Title>Assign Carer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="assign-carer-filter-bar" style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={matchFlightNumber}
                  onChange={(e) => setMatchFlightNumber(e.target.checked)}
                />
                Match exact flight number
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={matchPlanType}
                  onChange={(e) => setMatchPlanType(e.target.checked)}
                />
                Match plan type
              </label>
            </div>
            <div className="assign-carer-filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <input
                type="text"
                value={carerSearch}
                onChange={(e) => setCarerSearch(e.target.value)}
                placeholder="Search name/email/phone/flight"
                className="userlist-filter-input"
                style={{ flex: 1, padding: 8, borderRadius: 8 }}
              />
              <label>Gender:</label>
              <select
                value={carerGender}
                onChange={(e) => setCarerGender(e.target.value)}
                className="userlist-filter-select"
                style={{ padding: 8, borderRadius: 8 }}
              >
                <option value="">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <label>Language:</label>
              <input
                type="text"
                value={carerLanguage}
                onChange={(e) => setCarerLanguage(e.target.value)}
                placeholder="e.g., English"
                className="userlist-filter-input"
                style={{ width: 200, padding: 8, borderRadius: 8 }}
              />
            </div>
            <form onSubmit={formik.handleSubmit}>
              <div className="user_list_wrap table-responsive">
                <table className="table table-stripd">
                  <thead>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Language</th>
                    <th>Gender</th>
                    <th>Plan Types</th>
                    <th>Medical Background</th>
                    <th>Flight Number</th>
                    <th>Departure Date</th>

                    <th>Actions</th>
                  </thead>
                  <tbody>
                    {Array.isArray(displayedCarers) && displayedCarers.map(function (object, i) {
                        return (
                          <tr key={object?._id || i}>
                            <td>
                              {FirstLetterCapital(
                                object?.first_name + " " + object?.last_name
                              )}{" "}
                            </td>
                            <td>{object?.email}</td>
                            <td>
                              {object?.phone?.country_code !== undefined && (
                                <>
                                  <Flag
                                    country={Contry(
                                      object?.phone?.country_code
                                    )}
                                    size={20}
                                  />
                                  {object?.phone?.country_code +
                                    "  " +
                                    object?.phone?.number}
                                </>
                              )}
                            </td>
                            <td>
                              {" "}
                              {object.language_preferences
                                ?.map(
                                  (language) =>
                                    language.charAt(0).toUpperCase() +
                                    language.slice(1)
                                )
                                .join(", ")}{" "}
                            </td>
                            <td>{FirstLetterCapital(object?.gender)}</td>
                            <td>
                              {(() => {
                                const pkgs =
                                  object?.flight_details?.packages ||
                                  object?.flight_details?.offered_plan_types ||
                                  [];
                                return Array.isArray(pkgs) && pkgs.length
                                  ? pkgs
                                      .map(
                                        (p) =>
                                          p && typeof p === "string"
                                            ? p.charAt(0).toUpperCase() + p.slice(1)
                                            : p
                                      )
                                      .join(", ")
                                  : "N/A";
                              })()}
                            </td>
                            <td>{object?.profession?.name ?? "N/A"}</td>
                            <td>
                              {object?.flight_details?.flight_number ?? "N/A"}
                            </td>
                            <td>
                              {moment(
                                object?.flight_details?.departure_date
                              ).format("DD-MM-YYYY") ?? "N/A"}
                            </td>
                            <td>
                              {" "}
                              <button
                                type="submit"
                                className=" common-btn"
                                onClick={() => {
                                  formik.setFieldValue("carer_id", object?._id);
                                }}
                              >
                                Allocate
                              </button>
                            </td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            </form>
          </Modal.Body>
        </Modal>
        <Modal show={canclerequestshow} onHide={handleCancelBookingClose}>
          <Modal.Header closeButton>
            <Modal.Title>Cancel Booking Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={cancelFormik.handleSubmit}>
              {/* Flat Discount Rate Field */}
              <div className="mb-3">
                <label htmlFor="discountRate" className="form-label">
                  Select reason
                </label>
                <Searchable
                  value=""
                  placeholder="Search Reason" // by default "Search"
                  notFoundText="No result found" // by default "No result found"
                  options={resasonlist?.map((object, i) => ({
                    label: `${object?.reason} `, // Displayed text
                    value: object?._id || i, // Use object id or index as value
                  }))}
                  onSelect={(value) => {
                    cancelFormik.setFieldValue("reason_id", value);
                  }}
                  listMaxHeight={200} //by default 140
                  onChange={cancelFormik.handleChange}
                  onBlur={cancelFormik.handleBlur}
                  name="reason_id"
                />
              </div>
              {cancelFormik.touched.carer &&
                cancelFormik.submitCount > 0 &&
                cancelFormik.errors.reason_id && (
                  <div className="error text-danger">
                    {cancelFormik.errors.reason_id}
                  </div>
                )}
              <div className="mb-3">
                <label htmlFor="shortDescription" className="form-label">
                  Short Text Description
                </label>
                <textarea
                  className="form-control"
                  id="shortDescription"
                  rows="3"
                  placeholder="Enter a short description"
                  name="reason"
                  onChange={cancelFormik.handleChange}
                  value={cancelFormik.values.reason}
                  onBlur={cancelFormik.handleBlur}
                ></textarea>
                {cancelFormik.touched.reason &&
                  cancelFormik.submitCount > 0 &&
                  cancelFormik.errors.reason && (
                    <div className="error text-danger">
                      {cancelFormik.errors.reason}
                    </div>
                  )}
              </div>

              <div className="modal-footer">
                {/* Submit Button */}
                <button type="submit" className=" common-btn">
                  Submit
                </button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </LoadingOverlay>
      <ReactTooltip id="verification-tip" place="top" />
    </Layout>
  );
}
